import type { TripView } from './types';

// Pure trip-list algebra: the query key, the merge the WS push uses, and the
// PNR-group attribution the card renders. Kept OUT of the component and out of
// `applyEvent` so all three are unit-testable without React or a socket.

/**
 * TanStack Query key for a case's trips. One key per case (NOT per PNR group):
 * the BFF route is case-scoped, the drawer's group is a client-side slice of it,
 * and two drawers on the same case must share one cache entry so a `TripChanged`
 * push lands once for both.
 */
export function caseTripsKey(caseUrn: string): readonly unknown[] {
  return ['trips', caseUrn];
}

/**
 * Stable render order, matching the BFF's own: oldest request first, `urn` as the
 * tiebreak so two trips created in the same millisecond never swap places between
 * renders.
 */
export function sortTrips(trips: readonly TripView[]): TripView[] {
  return [...trips].sort(
    (a, b) => a.requestedAt.localeCompare(b.requestedAt) || a.urn.localeCompare(b.urn),
  );
}

/**
 * Fold one `TripChanged` payload into a cached list: replace the trip with the
 * same `urn`, or append it when the push is the first we've heard of it (an
 * AUTOMATIC dispatch fired by the domain while the drawer was already open).
 *
 * Whole-object replace, not a field merge — `TripChanged` carries the ENTIRE
 * `TripView`, so a merge would keep fields the producer has since dropped
 * (a `driverInfo` on a trip that just FAILED, say). Re-sorted on every fold so an
 * appended trip lands in its place rather than at the bottom.
 */
export function mergeTrip(trips: readonly TripView[] | undefined, trip: TripView): TripView[] {
  const current = trips ?? [];
  const index = current.findIndex((t) => t.urn === trip.urn);
  if (index === -1) return sortTrips([...current, trip]);
  const next = [...current];
  next[index] = trip;
  return sortTrips(next);
}

/**
 * Everything the card knows about WHICH passengers this drawer is showing. Built
 * from the roster's PNR group row; kept structural so the pure functions below
 * don't drag the whole disruption type graph into this module.
 */
export interface TripGroupIdentity {
  subCaseUrn?: string | undefined;
  /** Friendly PNR label, e.g. `AB12CD` — what a trip's `groupId` usually carries. */
  pnrLabel?: string | undefined;
  pnrUrn?: string | undefined;
  /** Every passenger URN on the roster for this PNR. */
  paxUrns: readonly string[];
}

export interface TripGroupSplit {
  /** Trips attributable to THIS PNR group — the card's primary list. */
  mine: TripView[];
  /** The rest of the case's trips (other PNR groups) — secondary, collapsed. */
  others: TripView[];
}

/**
 * Attribute a case's trips to the PNR group the drawer is showing.
 *
 * A trip belongs to this group when ANY of the three identities the producer may
 * carry matches: its `subCaseUrn` (the strongest — one sub-case IS one PNR), its
 * `passengerUrn` (present on every trip, and the identity a MANUAL dispatch from
 * this very card creates), or its `groupId` (the vendor-facing PNR label).
 *
 * Trips that match none are NOT dropped: they belong to the case's other PNR
 * groups and the card surfaces them collapsed, so an operator can see the whole
 * case's ground-transport picture without leaving the drawer — and so a producer
 * that stamps an identity we don't recognise degrades to "visible but not mine"
 * rather than to "invisible".
 */
export function splitTripsForGroup(
  trips: readonly TripView[] | undefined,
  identity: TripGroupIdentity,
): TripGroupSplit {
  const paxUrns = new Set(identity.paxUrns);
  const mine: TripView[] = [];
  const others: TripView[] = [];
  for (const trip of trips ?? []) {
    const isMine =
      (!!identity.subCaseUrn && trip.subCaseUrn === identity.subCaseUrn) ||
      paxUrns.has(trip.passengerUrn) ||
      (!!trip.groupId &&
        (trip.groupId === identity.pnrLabel ||
          trip.groupId === identity.pnrUrn ||
          // Fixtures + some vendors prefix the label ("PNR-AB12CD").
          (!!identity.pnrLabel && trip.groupId === `PNR-${identity.pnrLabel}`)));
    (isMine ? mine : others).push(trip);
  }
  return { mine, others };
}

/**
 * The passenger's live (non-terminal) trip on a leg, if any — what makes the
 * per-passenger dispatch button honest: a passenger who already has a car coming
 * is not offered another one (the BFF would idempotently hand back the same trip,
 * but an operator should not have to click to find that out).
 */
export function liveTripFor(
  trips: readonly TripView[] | undefined,
  passengerUrn: string,
): TripView | undefined {
  return (trips ?? []).find(
    (t) =>
      t.passengerUrn === passengerUrn &&
      t.status !== 'COMPLETED' &&
      t.status !== 'CANCELLED' &&
      t.status !== 'FAILED' &&
      t.status !== 'FAILED_COMPENSATED',
  );
}
